<script setup>
import { computed, onMounted, ref, watch } from 'vue'

const props = defineProps({
  modelValue: Boolean,
  id: { type: String, default: '' },
  checked: { type: Boolean, default: false },
  labelOn: { type: String, default: 'On' },
  labelOff: { type: String, default: 'Off' },
  name: { default: '' },
  value: { default: '' },
  styleOn: { type: String, default: 'primary' },
  styleOff: { type: String, default: 'default' },
  disabled: { type: Boolean, default: false },
  rounded: { type: Boolean, default: false },
  animate: { type: Boolean, default: false },
})

const DEFAULT_WIDTH = 50
const SWITCH_WIDTH = 30
const MIN_WIDTH = 50
const HORIZONTAL_PADDING = 5

const style = ref({})
const labelMaxWidth = ref(0)
const labelMaxChar = ref(0)
const inputModel = ref(Boolean(props.checked || props.modelValue))
const generatedId = ref('')
const toggleLabelOn = ref(null)
const toggleLabelOff = ref(null)

const emit = defineEmits(['update:modelValue'])

onMounted(() => {
  if (props.id === '') {
    generatedId.value =
      'stableEnhancedToggle_' + Math.random().toString(36).substring(2, 9)
  } else {
    generatedId.value = props.id
  }

  labelMaxWidth.value = getLabelMaxWidth()
  labelMaxChar.value = getLabelMaxChar()
  computeStyle()
})

watch(
  () => props.modelValue,
  (newValue) => {
    const normalizedValue = Boolean(newValue)
    if (normalizedValue !== inputModel.value) {
      inputModel.value = normalizedValue
    }
  },
)

watch(
  () => props.checked,
  (newValue) => {
    if (newValue === true && inputModel.value !== true) {
      inputModel.value = true
    }
  },
)

watch(
  () => props.labelOn,
  () => {
    updateLabels()
  },
)

watch(
  () => props.labelOff,
  () => {
    updateLabels()
  },
)

const computedClass = computed(() => {
  let resolvedClass = 'enhancedCheck-' + props.styleOff

  if (inputModel.value) {
    resolvedClass = 'enhancedCheck-' + props.styleOn
    resolvedClass += ' enhancedCheck-checked'
  }

  if (props.rounded) resolvedClass += ' enhancedCheck-rounded'
  if (props.animate) resolvedClass += ' enhancedCheck-animate'
  if (props.disabled) resolvedClass += ' enhancedCheck-disabled'

  return resolvedClass
})

const inputChange = () => {
  emit('update:modelValue', inputModel.value)
}

const getLabelMaxWidth = () => {
  if (
    typeof toggleLabelOn.value === 'undefined' ||
    toggleLabelOn.value == null
  ) {
    return DEFAULT_WIDTH
  }

  return (
    Math.max(
      toggleLabelOn.value.getBoundingClientRect().width,
      toggleLabelOff.value.getBoundingClientRect().width,
    ) - HORIZONTAL_PADDING
  )
}

const getLabelMaxChar = () => {
  return Math.max(props.labelOn.length, props.labelOff.length)
}

const updateLabels = () => {
  const newMaxChar = getLabelMaxChar()

  if (newMaxChar !== labelMaxChar.value) {
    labelMaxWidth.value =
      (labelMaxWidth.value / labelMaxChar.value) * newMaxChar
    labelMaxChar.value = newMaxChar

    if (labelMaxWidth.value < MIN_WIDTH) {
      labelMaxWidth.value = MIN_WIDTH
    }

    computeStyle()
  }
}

const computeStyle = () => {
  style.value = {
    '--labelWidth': labelMaxWidth.value + 'px',
    '--switchWidth': SWITCH_WIDTH + 'px',
    '--switchPos': labelMaxWidth.value + HORIZONTAL_PADDING + 'px',
    '--toggleWidth':
      labelMaxWidth.value + SWITCH_WIDTH / 2 + HORIZONTAL_PADDING + 'px',
  }
}
</script>

<template>
  <div
    class="enhancedCheck enhancedToggle"
    :class="computedClass"
    :style="style"
  >
    <label :for="generatedId">
      <input
        v-model="inputModel"
        type="checkbox"
        :id="generatedId"
        :name="name"
        :value="value"
        :disabled="disabled"
        :checked="inputModel"
        @change="inputChange()"
      />

      <span
        ref="toggleLabelOn"
        class="enhancedToggle-label labelOn"
        >{{ labelOn }}</span
      >
      <span class="enhancedToggle-switch"></span>
      <span
        ref="toggleLabelOff"
        class="enhancedToggle-label labelOff"
        >{{ labelOff }}</span
      >
    </label>
  </div>
</template>

<style lang="scss" scoped>
.enhancedToggle {
  width: var(--toggleWidth);

  .labelOn {
    width: var(--labelWidth);
    white-space: nowrap;
  }

  .labelOff {
    width: var(--labelWidth);
    white-space: nowrap;
  }

  .enhancedToggle-switch {
    width: var(--switchWidth);
    left: var(--switchPos);
  }
}

* {
  -webkit-box-sizing: content-box;
  -moz-box-sizing: content-box;
  box-sizing: content-box;
}

label {
  max-width: none;
  margin-bottom: 0;
}
</style>
